/**
 * シナリオ読み込み時の「動画生成モード」決定ロジック。
 *
 * ⚠️ レガシー読み込み専用 (P2 step 7-6・docs/features/
 * scene-location-and-production-mode-plan.md §9)。新規シナリオの正本は
 * `generation_metadata.production_mode` / `per_scene_default_mode`
 * (`features/scenarios/productionMode.ts` の `deriveProductionMode` /
 * `derivePerSceneDefaultMode` / `deriveLoadedVideoGenerationMode`)。この関数は
 * それらが未設定の旧データを読み込むためのフォールバック元としてのみ現役
 * (`deriveProductionMode` 等がこの関数を内部で呼ぶ)。UI 表示・生成分岐など新規の
 * 呼び出し箇所はこの関数を直接使わず、上記3関数 (または合成ヘルパー
 * `deriveLoadedVideoGenerationMode`) を使うこと。
 *
 * 正本は `generation_metadata.video_generation_mode` (作成時に backend が確実に書く)。
 * これを優先し、無い/不正な旧データのときだけ `frame_mode` から導出する。
 *
 * 背景: `scenarios.frame_mode` の storyboard/reference への昇格は、生成直後のフロント
 * PATCH やボード選択といった「作成とは別トランザクションの後追い書き込み」に依存して
 * いる。その書き込みが失敗すると、`gm.video_generation_mode='storyboard'` なのに
 * `frame_mode='first_only'` のまま残り、frame_mode だけを見るとフレームモードと誤判定
 * してしまう。これを防ぐため gm を優先する。
 *
 * 詳細: docs/requirements/storyboard-mode-loading-fix-plan.md
 */

/** ロード時に確定する動画生成モード (null を含まない) */
export type LoadedVideoGenerationMode = 'frame' | 'reference' | 'storyboard';

interface ScenarioModeSource {
  frame_mode?: string | null;
  generation_metadata?: Record<string, unknown> | null;
}

export function deriveVideoGenerationMode(
  scenario: ScenarioModeSource,
): LoadedVideoGenerationMode {
  const gmMode = scenario.generation_metadata?.video_generation_mode;
  if (gmMode === 'storyboard' || gmMode === 'reference' || gmMode === 'frame') {
    return gmMode;
  }
  // 旧データ (gm 未設定) のフォールバック: frame_mode から導出。
  // frame_mode は storyboard/reference 以外 (first_only 等) はすべて frame 扱い。
  if (scenario.frame_mode === 'reference') return 'reference';
  if (scenario.frame_mode === 'storyboard') return 'storyboard';
  return 'frame';
}

/**
 * シナリオ (生成フォーム / 作業画面) で扱う動画生成モード。
 *
 * UI state の `VideoGenerationMode` (components/generation/state/types.ts) は
 * プロンプト入力モード専用の `'motion'` (モーショングラフィックス・
 * docs/features/motion-graphics-mode-plan.md §5.1) を含むが、シナリオには
 * モーショングラフィックスという生成方法が無い。
 */
export type ScenarioVideoGenerationMode = LoadedVideoGenerationMode | null;

/**
 * UI state の動画生成モードをシナリオ側の値へ寄せる。`'motion'` は
 * プロンプト入力モード以外では起こり得ない前提だが、残留した場合は
 * プロンプト入力モードの既定 (AIおまかせ = `'reference'`) と同じ扱いにする
 * (計画 md §5.1「シナリオ生成フォームでは reference へ寄せる」)。
 */
export function toScenarioVideoGenerationMode(
  mode: ScenarioVideoGenerationMode | 'motion',
): ScenarioVideoGenerationMode {
  return mode === 'motion' ? 'reference' : mode;
}
